import gsap from "gsap";
import type { HistoricalTimelineDecade } from "../generated-types";
import {
	DECADE_HOVER_GRAPH_VIEWBOX_HEIGHT,
	DECADE_HOVER_GRAPH_VIEWBOX_WIDTH,
	getDecadeHoverGraphsMarkup,
} from "./decade-hover-graphs";
import {
	EXPAND_DURATION_MS,
	HOVER_GRAPH_DRAW_DURATION_S,
	HOVER_GRAPH_DRAW_STAGGER_S,
} from "./timeline-constants";
import { isRtlDocument, prefersReducedMotion } from "./timeline-utils";

function setGraphClipExpanded(clip: SVGRectElement): void {
	gsap.set(clip, {
		attr: {
			x: 0,
			y: 0,
			width: DECADE_HOVER_GRAPH_VIEWBOX_WIDTH,
			height: DECADE_HOVER_GRAPH_VIEWBOX_HEIGHT,
		},
	});
}

function setGraphClipCollapsed(clip: SVGRectElement, rtl: boolean): void {
	if (rtl) {
		gsap.set(clip, {
			attr: {
				x: DECADE_HOVER_GRAPH_VIEWBOX_WIDTH,
				y: 0,
				width: 0,
				height: DECADE_HOVER_GRAPH_VIEWBOX_HEIGHT,
			},
		});
		return;
	}

	gsap.set(clip, {
		attr: {
			x: 0,
			y: 0,
			width: 0,
			height: DECADE_HOVER_GRAPH_VIEWBOX_HEIGHT,
		},
	});
}

export function rewriteGraphSvgIds(container: HTMLElement, suffix: string): void {
	const idMap = new Map<string, string>();

	container.querySelectorAll<SVGElement>("[id]").forEach((element) => {
		const oldId = element.id;
		if (!oldId.startsWith("historical-timeline-graph")) {
			return;
		}

		const newId = `${oldId}__${suffix}`;
		idMap.set(oldId, newId);
		element.id = newId;
	});

	if (idMap.size === 0) {
		return;
	}

	const replaceUrlRefs = (value: string): string => {
		let next = value;

		idMap.forEach((newId, oldId) => {
			next = next.split(`url(#${oldId})`).join(`url(#${newId})`);
		});

		return next;
	};

	container.querySelectorAll<SVGElement>("*").forEach((element) => {
		for (const attribute of ["clip-path", "fill", "stroke", "mask", "href", "xlink:href"]) {
			const value = element.getAttribute(attribute);
			if (!value) {
				continue;
			}

			const replaced = replaceUrlRefs(value);
			if (replaced !== value) {
				element.setAttribute(attribute, replaced);
			}
		}
	});
}

export function refreshOverviewSlideGraphIds(scope: ParentNode): void {
	scope.querySelectorAll<HTMLElement>(".swiper-slide").forEach((slide, slideIndex) => {
		const graphsHost = slide.querySelector<HTMLElement>("[data-decade-graphs-host]");
		if (!graphsHost?.innerHTML.trim()) {
			return;
		}

		rewriteGraphSvgIds(graphsHost, `slide-${slideIndex}`);
	});
}

export function completeDecadeHoverGraphs(graphsHost: HTMLElement): void {
	const clips = Array.from(
		graphsHost.querySelectorAll<SVGRectElement>(".historical-timeline__decade-hover-graph-clip")
	);

	if (clips.length === 0) {
		return;
	}

	clips.forEach((clip) => {
		setGraphClipExpanded(clip);
	});
}

export function animateDecadeHoverGraphs(
	graphsHost: HTMLElement,
	baseDelayS = (EXPAND_DURATION_MS * 0.25) / 1000
): gsap.core.Timeline | undefined {
	const clips = Array.from(
		graphsHost.querySelectorAll<SVGRectElement>(".historical-timeline__decade-hover-graph-clip")
	);

	if (clips.length === 0) {
		return undefined;
	}

	if (prefersReducedMotion()) {
		completeDecadeHoverGraphs(graphsHost);
		return undefined;
	}

	const timeline = gsap.timeline();
	const baseDelay = baseDelayS;
	const rtl = isRtlDocument();

	clips.forEach((clip, index) => {
		const start = baseDelay + index * HOVER_GRAPH_DRAW_STAGGER_S;

		setGraphClipCollapsed(clip, rtl);

		if (rtl) {
			timeline.to(
				clip,
				{
					attr: { x: 0, width: DECADE_HOVER_GRAPH_VIEWBOX_WIDTH },
					duration: HOVER_GRAPH_DRAW_DURATION_S,
					ease: "power2.out",
				},
				start
			);
			return;
		}

		timeline.to(
			clip,
			{
				attr: { width: DECADE_HOVER_GRAPH_VIEWBOX_WIDTH },
				duration: HOVER_GRAPH_DRAW_DURATION_S,
				ease: "power2.out",
			},
			start
		);
	});

	return timeline;
}

export function renderDecadeHoverGraphsHost(
	graphsHost: HTMLElement,
	decade: HistoricalTimelineDecade,
	activeCategoryKeys: string[],
	idPrefix: string,
	shouldAnimate: boolean,
	animationBaseDelayS?: number
): gsap.core.Timeline | undefined {
	graphsHost.innerHTML = getDecadeHoverGraphsMarkup(decade, activeCategoryKeys, idPrefix);

	if (!graphsHost.innerHTML.trim()) {
		return undefined;
	}

	const graphInstanceKey = `${idPrefix}-${graphsHost.closest<HTMLElement>(".swiper-slide")?.dataset.swiperSlideIndex ?? "0"}`;
	rewriteGraphSvgIds(graphsHost, graphInstanceKey);

	const shouldRunDrawAnimation = shouldAnimate && !prefersReducedMotion();

	if (shouldRunDrawAnimation) {
		return animateDecadeHoverGraphs(graphsHost, animationBaseDelayS);
	}

	completeDecadeHoverGraphs(graphsHost);
	return undefined;
}
